"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { motion } from "framer-motion";
import AirdropConfig from "../components/AirdropConfig";
import AllowlistBatchManager, { AllowlistBatchRaw } from "../components/AllowlistBatchManager";
import AdditionalMediaUpload from "../components/AdditionalMediaUpload";
import { validateStrictAddress } from "../utils/addressValidation";
import { validateTokenIdFormat } from "../utils/tokenParsing";
import { AirdropEntry, AIRDROP_TOTAL } from "../types/OperationalData";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
interface AdditionalInfoStepProps {
  readonly airdropEntries: AirdropEntry[];
  readonly onAirdropEntriesChange: (entries: AirdropEntry[]) => void;
  readonly allowlistBatches: AllowlistBatchRaw[];
  readonly artworkCommentaryMedia: string[];
  readonly artworkCommentary: string;
  readonly onBatchesChange: (batches: AllowlistBatchRaw[]) => void;
  readonly onArtworkCommentaryMediaChange: (media: string[]) => void;
  readonly onArtworkCommentaryChange: (commentary: string) => void;
  readonly onBack: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting: boolean;
}
const AdditionalInfoStep: FC<AdditionalInfoStepProps> = t0 => {
  const $ = _c(39);
  const {
    airdropEntries,
    onAirdropEntriesChange,
    allowlistBatches,
    artworkCommentaryMedia,
    artworkCommentary,
    onBatchesChange,
    onArtworkCommentaryMediaChange,
    onArtworkCommentaryChange,
    onBack,
    onSubmit,
    isSubmitting
  } = t0;
  const getContractError = _temp;
  const getTokenIdsError = _temp2;
  let t1;
  if ($[0] !== airdropEntries || $[1] !== allowlistBatches) {
    t1 = () => {
      const hasInvalidCount = airdropEntries.some(_temp3);
      if (hasInvalidCount) {
        return false;
      }
      const totalAllocated = airdropEntries.reduce(_temp4, 0);
      if (totalAllocated !== AIRDROP_TOTAL) {
        return false;
      }
      const hasEntryWithCountButNoValidAddress = airdropEntries.some(_temp5);
      if (hasEntryWithCountButNoValidAddress) {
        return false;
      }
      const hasEntryWithAddressButNoCount = airdropEntries.some(_temp6);
      if (hasEntryWithAddressButNoCount) {
        return false;
      }
      const hasInvalidAddress = airdropEntries.some(_temp7);
      if (hasInvalidAddress) {
        return false;
      }
      const hasInvalidBatch = allowlistBatches.some(_temp8);
      if (hasInvalidBatch) {
        return false;
      }
      const hasInvalidTokenIds = allowlistBatches.some(_temp9);
      if (hasInvalidTokenIds) {
        return false;
      }
      return true;
    };
    $[0] = airdropEntries;
    $[1] = allowlistBatches;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isFormValid = t1;
  let t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 20
    };
    t3 = {
      opacity: 1,
      y: 0
    };
    t4 = {
      opacity: 0,
      y: -20
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="tw-text-sm tw-text-iron-400">Complete the following details for distribution and storytelling purposes.</p>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== airdropEntries || $[8] !== onAirdropEntriesChange) {
    t6 = <AirdropConfig entries={airdropEntries} onEntriesChange={onAirdropEntriesChange} />;
    $[7] = airdropEntries;
    $[8] = onAirdropEntriesChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== allowlistBatches) {
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = batch_1 => {
        const contractError = getContractError(batch_1.contract);
        const tokenIdsError = getTokenIdsError(batch_1.token_ids_raw);
        return {
          ...(contractError ? {
            contract: contractError
          } : {}),
          ...(tokenIdsError ? {
            token_ids: tokenIdsError
          } : {})
        };
      };
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t7 = allowlistBatches.map(t8);
    $[10] = allowlistBatches;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[13] !== allowlistBatches || $[14] !== onBatchesChange || $[15] !== t7) {
    t8 = <AllowlistBatchManager batches={allowlistBatches} onBatchesChange={onBatchesChange} errors={t7} />;
    $[13] = allowlistBatches;
    $[14] = onBatchesChange;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== artworkCommentary || $[18] !== artworkCommentaryMedia || $[19] !== onArtworkCommentaryChange || $[20] !== onArtworkCommentaryMediaChange) {
    t9 = <AdditionalMediaUpload artworkCommentaryMedia={artworkCommentaryMedia} artworkCommentary={artworkCommentary} onArtworkCommentaryMediaChange={onArtworkCommentaryMediaChange} onArtworkCommentaryChange={onArtworkCommentaryChange} />;
    $[17] = artworkCommentary;
    $[18] = artworkCommentaryMedia;
    $[19] = onArtworkCommentaryChange;
    $[20] = onArtworkCommentaryMediaChange;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-2 tw-space-y-8 tw-scrollbar-thin tw-scrollbar-thumb-iron-700 tw-scrollbar-track-iron-900">{t5}{t6}{t8}{t9}</div>;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== isSubmitting || $[27] !== onBack) {
    t11 = <SecondaryButton onClicked={onBack} disabled={isSubmitting}>Back</SecondaryButton>;
    $[26] = isSubmitting;
    $[27] = onBack;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = !isFormValid();
  let t13;
  if ($[29] !== isSubmitting || $[30] !== onSubmit || $[31] !== t12) {
    t13 = <PrimaryButton onClicked={onSubmit} disabled={t12} loading={isSubmitting} padding="tw-px-6 tw-py-3">Submit Artwork</PrimaryButton>;
    $[29] = isSubmitting;
    $[30] = onSubmit;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t13) {
    t14 = <div className="tw-flex tw-items-center tw-justify-between tw-pt-6 tw-px-4 tw-border-t tw-border-iron-800 tw-mt-auto">{t11}{t13}</div>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t14) {
    t15 = <motion.div initial={t2} animate={t3} exit={t4} className="tw-flex tw-flex-col tw-h-full tw-max-w-4xl tw-mx-auto tw-pb-8">{t10}{t14}</motion.div>;
    $[36] = t10;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
};
export default AdditionalInfoStep;
function _temp(address) {
  if (!address) {
    return;
  }
  if (!validateStrictAddress(address)) {
    return "Invalid contract address";
  }
}
function _temp2(tokenIds) {
  return validateTokenIdFormat(tokenIds) || undefined;
}
function _temp3(e) {
  return !Number.isFinite(e.count) || e.count < 0 || !Number.isInteger(e.count);
}
function _temp4(sum, e_0) {
  return sum + (e_0.count ?? 0);
}
function _temp5(e_1) {
  return e_1.count > 0 && (!e_1.address || !validateStrictAddress(e_1.address));
}
function _temp6(e_2) {
  return e_2.address && validateStrictAddress(e_2.address) && (!e_2.count || e_2.count <= 0);
}
function _temp7(e_3) {
  return e_3.address && !validateStrictAddress(e_3.address);
}
function _temp8(batch) {
  return !batch.contract || !validateStrictAddress(batch.contract);
}
function _temp9(batch_0) {
  return validateTokenIdFormat(batch_0.token_ids_raw) !== null;
}